import { useMemo } from "react";
import { Pencil, Trash, Users, Mail, MessageCircle, PowerOff, Power } from "lucide-react";

import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";

export function useDistributionListColumns(tableState, { onEdit, onDelete, onToggleActive }) {
	return useMemo(
		() => [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: "5%",
				Cell: ({ row }) => {
					return <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>;
				},
			},
			{
				Header: "Nombre",
				accessor: "name",
				width: "15%",
			},
			{ Header: "Descripción", accessor: "description", width: "20%" },
			{
				Header: "Campos",
				accessor: "estates_parents",
				width: "24%",
				disableSortBy: true,
				Cell: ({ value }) => {
					const names = Array.isArray(value) ? value.map((e) => e?.estate_parent_name || e?.name).filter(Boolean) : [];
					if (!names.length) return <span>-</span>;
					return (
						<div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
							{names.map((name, idx) => (
								<CustomBadge key={idx} variant="neutral">
									{name}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Contactos",
				accessor: "member_count",
				width: "10%",
				meta: { cellClassName: "tdCenter" },
				disableSortBy: true,
				Cell: ({ value }) => {
					return (
						<div style={{ display: "flex", justifyContent: "center" }}>
							<CustomBadge variant="neutral" icon={Users}>
								{value || 0}
							</CustomBadge>
						</div>
					);
				},
			},
			{
				Header: "Medio de Notificación",
				accessor: "notification_type",
				width: "12%",
				meta: { cellClassName: "tdCenter" },
				disableSortBy: true,
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const types = value
						.split(",")
						.map((t) => t.trim())
						.filter(Boolean);

					return (
						<div style={{ display: "flex", justifyContent: "center", gap: "0.25rem", flexWrap: "wrap" }}>
							{types.map((type, idx) => {
								const isWhatsapp = type.toLowerCase() === "whatsapp";
								const Icon = isWhatsapp ? MessageCircle : Mail;
								return (
									<CustomBadge key={idx} variant={isWhatsapp ? "success" : "info"} startIcon={<Icon size={14} />}>
										{isWhatsapp ? "WhatsApp" : "Email"}
									</CustomBadge>
								);
							})}
						</div>
					);
				},
			},
			{
				Header: "Estado",
				accessor: "is_active",
				width: "8%",
				meta: { cellClassName: "tdCenter" },
				disableSortBy: true,
				Cell: ({ value }) => {
					return (
						<div style={{ display: "flex", justifyContent: "center" }}>
							<CustomBadge variant={value ? "primary" : "danger"}>{value ? "Activo" : "Inactivo"}</CustomBadge>
						</div>
					);
				},
			},
			{
				Header: "Acción",
				disableSortBy: true,
				accessor: "id",
				width: "7%",
				Cell: ({ value, row }) => {
					const isActive = row.original.is_active;
					return (
						<CustomDropdown
							title="Acciones"
							items={[
								{
									label: "Editar",
									action: () => onEdit(row.original.slug),
									icon: Pencil,
								},
								{
									label: isActive ? "Desactivar" : "Activar",
									action: () => onToggleActive(value, !isActive),
									icon: isActive ? PowerOff : Power,
								},
								{
									label: "Eliminar",
									action: () => onDelete(value),
									icon: Trash,
									danger: true,
								},
							]}
							align="right"
						/>
					);
				},
			},
			{
				Header: "",
				accessor: "_expand",
				disableSortBy: true,
				width: "3%",
				Cell: () => null,
			},
		],
		[onEdit, onDelete, onToggleActive, tableState.page, tableState.per_page],
	);
}

export default useDistributionListColumns;
